import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, X, Loader2 } from 'lucide-react';
import { panelPath } from '../config/paths';
import { useAuth } from '../context/AuthContext';
import { resumeSubscription } from '../api/client';
import { AppError } from '../api/request';

export interface UpgradePlanModalProps {
  open: boolean;
  onClose: () => void;
}

export default function UpgradePlanModal({ open, onClose }: UpgradePlanModalProps) {
  const { t } = useTranslation();
  const { user, updateUser } = useAuth();
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const showReactivate = user?.canReactivateSubscription === true;

  const handleResume = async () => {
    setResumeError(null);
    setResumeLoading(true);
    try {
      const updated = await resumeSubscription();
      updateUser(updated);
      onClose();
    } catch (err) {
      setResumeError(err instanceof AppError ? err.userMessage : t('profile.reactivateError'));
    } finally {
      setResumeLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-plan-title"
    >
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />
      <div className="relative w-full max-w-md rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:text-gray-300 dark:hover:bg-gray-700 transition-colors"
          aria-label={t('common.close')}
        >
          <X className="h-5 w-5" />
        </button>
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center">
            <Sparkles className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <h2 id="upgrade-plan-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
              {showReactivate ? t('planPurchase.reactivateTitle') : t('planUpgrade.title')}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-5">
              {showReactivate ? t('planPurchase.reactivateSubtitle') : t('planUpgrade.description')}
            </p>
            {resumeError && (
              <p className="text-sm text-red-600 dark:text-red-400 mb-3">{resumeError}</p>
            )}
            {showReactivate ? (
              <button
                type="button"
                onClick={handleResume}
                disabled={resumeLoading}
                className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-xl font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-70"
              >
                {resumeLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
                {resumeLoading ? t('common.loading') : t('profile.reactivateSubscription')}
              </button>
            ) : (
              <Link
                to={panelPath('upgrade')}
                className="inline-flex items-center justify-center w-full px-4 py-2.5 rounded-xl font-medium bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 transition-colors"
                onClick={onClose}
              >
                {t('planUpgrade.upgradeToPro')}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
